import React, { useEffect, useState } from "react"
import { SHA1 } from "crypto-js"
import { isMobile, useMobileOrientation } from "react-device-detect"
import Avatar from "@mui/material/Avatar"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import LinearProgress from "@mui/material/LinearProgress"
import DialogContent from "@mui/material/DialogContent"

import "./App.css"
import UAParser from "ua-parser-js"
import {
  clearHistoryAndNavigate,
  getData,
  getUrlInfo,
  reportSocialLandingPageButtonClicked,
} from "./connection"

export enum EViewState {
  loading = "loading",
  error = "error",
  expired = "expired",
  continue = "continue",
  twoSteps = "twoSteps",
  use_lp = "use_lp",
}

export interface IViewState {
  state: EViewState
  data?: string
  logo?: string
  toApp?: string
  toStore?: string
}
interface IIosSocialLandingPageButton {
  text?: string
  backgroundColor?: string
  fontColor?: string
  font?: string
  fontWeight?: string
}

function App() {
  const [backgroundImageUrl, setBackgroundImageUrl] = useState("")
  const [useSmallLoader, setUseSmallLoader] = useState(true)
  const [showPoweredBy, setShowPoweredBy] = useState(true)
  const [viewState, setViewState] = useState<IViewState>({
    state: EViewState.loading,
  })

  const [buttonsDesign, setButtonsDesign] = useState<
    | {
        openApp?: IIosSocialLandingPageButton
        downloadApp?: IIosSocialLandingPageButton
      }
    | undefined
  >(undefined)

  const [orgIdValue, setOrgIdValue] = useState("")
  const [linkIdValue, setLinkIdValue] = useState("")

  const { isPortrait } = useMobileOrientation()

  useEffect(() => {
    console.log(`Version: ${process.env.REACT_APP_VERSION}`)
    run()
    getBackgroundImage()
  }, [])

  useEffect(() => {
    console.log("viewState", viewState)
  }, [viewState])

  const init = async (): Promise<IViewState> => {
    try {
      const {
        d10xLinksBranding,
        linkId,
      }: {
        d10xLinksBranding: string
        linkId: string
      } = getUrlInfo()

      const url = new URL(window.location.href)
      const urlParams = url.searchParams

      const logo = urlParams.get("logo")
      const twoSteps = urlParams.get("twoSteps")
      const shagran = urlParams.get("shagran")
      const buttonsDesignStr = urlParams.get("buttonsDesign")
      let buttonsDesign
      if (buttonsDesignStr) {
        try {
          const buttonsDesignDecoded = decodeURIComponent(buttonsDesignStr)
          buttonsDesign = JSON.parse(buttonsDesignDecoded)
        } catch (error) {
          console.error("Error parsing buttonsDesign", error)
        }
      }
      if (buttonsDesign) {
        setButtonsDesign(buttonsDesign)
      }

      const mappingRes = await fetch(`${process.env.PUBLIC_URL}/brndtoom.json`)
      const mapping = await mappingRes.json()
      const orgIdKey = `B${SHA1(d10xLinksBranding).toString()}`
      const orgId = mapping[orgIdKey as keyof typeof mapping]
      setOrgIdValue(orgId)
      setLinkIdValue(linkId)

      if (shagran === "false") {
        setShowPoweredBy(false)
      }

      const uaData: UAParser.IResult | undefined = UAParser(navigator.userAgent)
      const usesExternalBrowser =
        uaData?.browser?.name !== "Facebook" &&
        uaData?.browser?.name !== "Messenger"

      if (!usesExternalBrowser && twoSteps === "true") {
        return {
          state: EViewState.twoSteps,
          logo: logo || undefined,
        }
      }

      // ?lp=true&to_app=flkjfj&to_store=lkgfjjgl
      const use_lp = urlParams.get("use_lp")
      const toApp = urlParams.get("to_app") || undefined
      const toStore = urlParams.get("to_store") || undefined

      if (use_lp === "true") {
        return {
          state: EViewState.use_lp,
          toApp,
          toStore,
          logo: logo || undefined,
        }
      }

      const res = await getData({
        d10xLinksBranding,
        linkId,
      })

      if (res.data) {
        clearHistoryAndNavigate(res.data)
        return { ...res }
      }

      return { state: EViewState.error }
    } catch (error) {
      return { state: EViewState.error }
    }
  }

  const run = async () => {
    const res = await init()
    setViewState(res)
  }

  const getBackgroundImage = async () => {
    const res = await fetch(`${process.env.PUBLIC_URL}/brndtoom.json`)
    const mapping = await res.json()

    const urlParts = window.location.pathname.split("/").slice(1)
    const brand = urlParts[0]

    const url = new URL(window.location.href)
    const urlParams = url.searchParams
    const use_lp = urlParams.get("use_lp") || "false"

    const prefix = use_lp === "true" ? "iosLandingPage" : "d10x.co"
    let fileName = ""

    if (use_lp === "true") {
      fileName = isPortrait ? "bgIosPortrait" : "bgIosLandscape"
    } else {
      fileName = isMobile ? "bgMobile" : "bgDesktop"
    }

    if (brand) {
      const orgIdKey = `B${SHA1(brand).toString()}`
      const orgId = mapping[orgIdKey as keyof typeof mapping]

      const env =
        process.env.REACT_APP_ENV === "dev"
          ? "/dev"
          : process.env.REACT_APP_ENV === "stg"
          ? "/stg"
          : ""

      const cacheBuster = `?cb=${new Date().getTime()}`
      const url = `https://res.cloudinary.com/dynamofyi/image/upload/f_auto,q_auto${env}/organizations/${orgId}/${prefix}/default/${fileName}${cacheBuster}`
      setBackgroundImageUrl(url)
      const imageRes = await fetch(url, { method: "HEAD" })
      if (imageRes.ok) {
        setUseSmallLoader(true)
      } else {
        setUseSmallLoader(false)
      }
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        paddingBottom: 10,
        flex: 1,
        backgroundImage:
          viewState.state === EViewState.twoSteps
            ? "none"
            : `url(${backgroundImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {viewState.state === EViewState.twoSteps && (
        <div
          style={{
            flex: 1,
            height: "1000px",
            padding: "0 20px",
            display: "flex",
            position: "relative",
          }}
        >
          <div className="message">
            {viewState.logo && (
              <img
                src={viewState.logo}
                alt="logo"
                style={{
                  width: "80px",
                  height: "auto",
                  marginBottom: 10,
                }}
              />
            )}
            <p>
              Hi! If you&apos;ve reached this screen, it means the button
              isn&apos;t working properly.
            </p>
            <p>To fix it, please tap the three-dot icon and select</p>
            <p>&apos;Open in external browser&apos;.</p>
          </div>
        </div>
      )}
      {viewState.state === EViewState.use_lp && (
        <div
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            padding: "0 20px",
            alignSelf: "stretch",
          }}
        >
          {viewState.logo && (
            <img
              src={viewState.logo}
              alt="logo"
              style={{
                width: "80px",
                height: "auto",
                marginBottom: 10,
              }}
            />
          )}
          <button
            style={{
              width: "100%",
              padding: "12px 0",
              marginBottom: "15px",
              background: buttonsDesign?.openApp?.backgroundColor || "#fff",
              color: buttonsDesign?.openApp?.fontColor || "#000",
              maxWidth: "300px",
              fontSize: "16px",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontFamily: buttonsDesign?.openApp?.font || "inherit",
              fontWeight: buttonsDesign?.openApp?.fontWeight || "bold",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.4)",
            }}
            onClick={() => {
              if (viewState.toApp) {
                reportSocialLandingPageButtonClicked({
                  orgId: orgIdValue,
                  identifier: linkIdValue,
                  button: "App",
                })
                clearHistoryAndNavigate(viewState.toApp)
              }
            }}
          >
            {buttonsDesign?.openApp?.text || "Open App"}
          </button>
          <button
            style={{
              width: "100%",
              padding: "12px 0",
              background: buttonsDesign?.downloadApp?.backgroundColor || "#fff",
              color: buttonsDesign?.downloadApp?.fontColor || "#000",
              maxWidth: "300px",
              fontSize: "16px",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontFamily: buttonsDesign?.downloadApp?.font || "inherit",
              fontWeight: buttonsDesign?.downloadApp?.fontWeight || "bold",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.4)",
            }}
            onClick={() => {
              if (viewState.toStore) {
                reportSocialLandingPageButtonClicked({
                  orgId: orgIdValue,
                  identifier: linkIdValue,
                  button: "Store",
                })
                clearHistoryAndNavigate(viewState.toStore)
              }
            }}
          >
            {buttonsDesign?.downloadApp?.text || "Download App"}
          </button>
        </div>
      )}
      {viewState.state !== EViewState.continue &&
        viewState.state !== EViewState.twoSteps &&
        viewState.state !== EViewState.use_lp && <div style={{ flex: 1 }} />}
      {useSmallLoader && viewState.state === EViewState.loading && (
        <div
          style={{
            width: "100%",
            maxWidth: "300px",
            marginBottom: "10px",
            backgroundColor: "#faa",
          }}
        >
          <LinearProgress style={{ backgroundColor: "#fff" }} />
        </div>
      )}
      {showPoweredBy && (
        <div
          style={{
            textAlign: "center",
            textShadow: "1px 1px 2px #000",
            textDecoration: "none",
            fontSize: "15px",
            fontFamily: "Avenir",
            color: "#fff",
          }}
        >
          Powered by Dynamo10x
        </div>
      )}
      <Dialog
        open={viewState.state === EViewState.error}
        PaperProps={{
          style: {
            borderRadius: 20,
          },
        }}
      >
        <DialogContent
          style={{
            padding: "40px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Avatar
            style={{
              width: "60px",
              height: "60px",
            }}
            src={`${process.env.PUBLIC_URL}/images/error.svg`}
          />
          <Typography
            variant="h6"
            style={{
              fontFamily: "Avenir",
              margin: "30px 0",
              textAlign: "center",
            }}
          >
            Oops, something is wrong
          </Typography>

          <Button
            variant="contained"
            style={{
              backgroundColor: "#000",
              borderRadius: 10,
              textTransform: "none",
            }}
            onClick={() => {
              setViewState({ state: EViewState.loading })
              run()
            }}
          >
            Try Again
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog
        open={viewState.state === EViewState.expired}
        PaperProps={{
          style: {
            borderRadius: 20,
          },
        }}
      >
        <DialogContent
          style={{
            padding: "40px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Avatar
            variant="square"
            style={{
              width: "74px",
              height: "70px",
            }}
            src={`${process.env.PUBLIC_URL}/images/expired.svg`}
          />
          <Typography
            variant="h6"
            style={{
              fontFamily: "Avenir",
              margin: "30px 0",
              textAlign: "center",
            }}
          >
            Link expired
          </Typography>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default App
